import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
  Button,
  Space,
  Alert,
} from 'antd';
import { SaveOutlined, SendOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Option } = Select;

interface DetailedAssessmentStepProps {
  module: string | null;
  projectCode: string;
}

const DetailedAssessmentStep: React.FC<DetailedAssessmentStepProps> = ({
  module,
  projectCode,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // 如果没有选择模块，显示模块选择提示
  if (!module) {
    return (
      <Alert
        message="请选择要填写的模块"
        description="请从左侧导航中选择具体的评估模块开始填写"
        type="info"
        showIcon
      />
    );
  }

  // 渲染不同模块的内容
  const renderModuleContent = () => {
    switch (module) {
      case 'issuer_analysis':
        return (
          <Card title="发行主体分析" style={{ marginBottom: '24px' }}>
            <Form form={form} layout="vertical">
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Form.Item
                    label="公司法定全称（中文）"
                    name="companyNameCN"
                    rules={[{ required: true, message: '请输入公司中文全称' }]}
                  >
                    <Input placeholder="请输入公司中文全称" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="公司法定全称（英文）"
                    name="companyNameEN"
                    rules={[{ required: true, message: '请输入公司英文全称' }]}
                  >
                    <Input placeholder="请输入公司英文全称" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="注册资本（万元）"
                    name="registeredCapital"
                    rules={[{ required: true, message: '请输入注册资本' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="请输入注册资本"
                      min={0}
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="控股股东持股比例（%）"
                    name="shareholderRatio"
                    rules={[{ required: true, message: '请输入持股比例' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="请输入持股比例"
                      min={0}
                      max={100}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        );

      case 'financial_analysis':
        return (
          <Card title="财务状况分析" style={{ marginBottom: '24px' }}>
            <Form form={form} layout="vertical">
              <Row gutter={[24, 16]}>
                <Col span={8}>
                  <Form.Item
                    label="总资产（万元）"
                    name="totalAssets"
                    rules={[{ required: true, message: '请输入总资产' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="请输入总资产"
                      min={0}
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="总负债（万元）"
                    name="totalLiabilities"
                    rules={[{ required: true, message: '请输入总负债' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="请输入总负债"
                      min={0}
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label="资产负债率（%）"
                    name="debtRatio"
                    rules={[{ required: true, message: '请输入资产负债率' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="请输入资产负债率"
                      min={0}
                      max={100}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        );

      case 'green_project':
        return (
          <Card title="绿色项目评估" style={{ marginBottom: '24px' }}>
            <Form form={form} layout="vertical">
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Form.Item
                    label="项目名称"
                    name="projectName"
                    rules={[{ required: true, message: '请输入项目名称' }]}
                  >
                    <Input placeholder="请输入绿色项目名称" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="项目类型"
                    name="projectType"
                    rules={[{ required: true, message: '请选择项目类型' }]}
                  >
                    <Select placeholder="请选择项目类型">
                      <Option value="光伏发电">光伏发电</Option>
                      <Option value="风力发电">风力发电</Option>
                      <Option value="节能改造">节能改造</Option>
                      <Option value="污水处理">污水处理</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="项目总投资（万元）"
                    name="totalInvestment"
                    rules={[{ required: true, message: '请输入项目总投资' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="请输入项目总投资"
                      min={0}
                      formatter={value =>
                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="年减排CO₂（万吨）" name="co2Reduction">
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="请输入年减排CO₂"
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        );

      case 'issuance_plan':
        return (
          <Card title="发行方案设计" style={{ marginBottom: '24px' }}>
            <Form form={form} layout="vertical">
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Form.Item
                    label="计划发行规模"
                    name="issuanceSize"
                    rules={[{ required: true, message: '请选择发行规模' }]}
                  >
                    <Select placeholder="请选择计划发行规模">
                      <Option value="3-5亿美元">3-5亿美元</Option>
                      <Option value="5-10亿美元">5-10亿美元</Option>
                      <Option value="10亿美元以上">10亿美元以上</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="计划发行期限"
                    name="issuanceTerm"
                    rules={[{ required: true, message: '请选择发行期限' }]}
                  >
                    <Select placeholder="请选择计划发行期限">
                      <Option value="3年">3年</Option>
                      <Option value="5年">5年</Option>
                      <Option value="7年">7年</Option>
                      <Option value="10年">10年</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        );

      case 'service_requirements':
        return (
          <Card title="中介服务需求" style={{ marginBottom: '24px' }}>
            <Form form={form} layout="vertical">
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Form.Item
                    label="承销商偏好"
                    name="underwriterPreference"
                    rules={[{ required: true, message: '请选择承销商偏好' }]}
                  >
                    <Select placeholder="请选择承销商偏好">
                      <Option value="国际投行">国际投行</Option>
                      <Option value="中资投行">中资投行</Option>
                      <Option value="混合承销团">混合承销团</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="评级机构偏好"
                    name="ratingAgency"
                    rules={[{ required: true, message: '请选择评级机构' }]}
                  >
                    <Select placeholder="请选择评级机构偏好">
                      <Option value="穆迪">穆迪</Option>
                      <Option value="标普">标普</Option>
                      <Option value="惠誉">惠誉</Option>
                      <Option value="中诚信">中诚信</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        );

      case 'risk_management':
        return (
          <Card title="风险识别与应对" style={{ marginBottom: '24px' }}>
            <Form form={form} layout="vertical">
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <Form.Item
                    label="监管审批状态"
                    name="regulatoryStatus"
                    rules={[{ required: true, message: '请选择监管审批状态' }]}
                  >
                    <Select placeholder="请选择监管审批状态">
                      <Option value="已获得全部审批">已获得全部审批</Option>
                      <Option value="部分审批待获得">部分审批待获得</Option>
                      <Option value="审批进行中">审批进行中</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="环保合规情况"
                    name="environmentalCompliance"
                    rules={[{ required: true, message: '请选择环保合规情况' }]}
                  >
                    <Select placeholder="请选择环保合规情况">
                      <Option value="完全合规">完全合规</Option>
                      <Option value="基本合规">基本合规</Option>
                      <Option value="存在轻微问题">存在轻微问题</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="风险应对措施" name="riskMitigation">
                    <TextArea
                      rows={4}
                      placeholder="请描述主要风险因素的应对措施..."
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        );

      default:
        return (
          <Card>
            <Alert
              message="暂无内容"
              description={`模块 "${module}" 暂无可用内容`}
              type="info"
              showIcon
            />
          </Card>
        );
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // TODO: 调用API保存数据
      console.log('保存模块数据:', { module, values });

      // 模拟API调用
      setTimeout(() => {
        setLoading(false);
        console.log('保存成功');
      }, 1000);
    } catch (error) {
      console.error('保存失败:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // TODO: 调用API提交数据
      console.log('提交模块数据:', { module, values });

      // 模拟API调用
      setTimeout(() => {
        setLoading(false);
        console.log('提交成功');
      }, 1000);
    } catch (error) {
      console.error('提交失败:', error);
      setLoading(false);
    }
  };

  return (
    <div>
      {renderModuleContent()}

      {/* 操作按钮 */}
      <Card>
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={loading}
            >
              保存草稿
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSubmit}
              loading={loading}
            >
              提交模块
            </Button>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default DetailedAssessmentStep;
